import { each, forIn, get, isEmpty, mapValues, set } from "lodash";
import validate from "validate.js";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints } from "../../utils/validation";

const outsideListingBroker = {
    "brokerAgentKey.person.firstName": {},
    "brokerAgentKey.person.lastName": {},
    "company.displayName": {},
};

export const mixedUseConstraints = {
    sf: {
        ...constraints.number,
    },
    unitCount: {
        ...constraints.id,
    },
};

export const portfolioConstraints = {
    propertyId: {
        ...constraints.id,
    },
};

export const condoConstraints = {
    condoNumber: {},
    condoSf: {
        ...constraints.number,
    },
};

const listingExpirationConstraint = {
    expirationDate: {
        ...constraints.date,
    },
};

export const listingConstraints = {
    Industrial: {
        "attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "attributes.blendedRate": {
            ...constraints.number,
        },
        "attributes.ampsNum": {
            ...constraints.number,
        },
        "attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    Land: {
        //verified
        "attributes.sfAvail": {
            ...constraints.id,
        },
        "attributes.totalAcres": {
            ...constraints.number,
        },
    },
    Multifamily: {
        //verified
        "attributes.multifamilyUnitCount": {
            ...constraints.id,
        },
        "attributes.multifamilySf": {
            ...constraints.number,
        },
        "attributes.wetlandsPct": {
            ...constraints.number,
        },
    },
    Hospitality: {
        //listing-specific
        "attributes.hospitalitySf": {
            ...constraints.id,
        },
        "attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "attributes.roomCount": {
            ...constraints.id,
        },
    },
    Educational: {
        "attributes.classroomCount": {
            ...constraints.wholeNumber,
        },
        "attributes.auditoriumSeatsCount": {
            ...constraints.wholeNumber,
        },
    },
    listingInformation: {
        dateListed: {
            ...constraints.date,
        },
        dateAvailable: {
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.date,
        },
        leaseExpirationDate: {
            ...constraints.date,
        },
        "attributes.numBldg": {
            ...constraints.number,
        },
        "attributes.lotAcres": {
            ...constraints.number,
        },
        "attributes.lotSf": {
            ...constraints.number,
        },
        sfAvail: {
            ...constraints.positiveNumber,
        },
        sfDivisibleTo: {
            ...constraints.number,
        },
        sellerLandlordCommonCompanyName: {
            ...constraints.maxLength,
        },
        sellerLandlordLegalCompanyName: {
            ...constraints.maxLength,
        },
    },
    parkingInformation: {
        "attributes.parkingTotal": {
            ...constraints.number,
        },
        "attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
    },
    listingFinancial: {
        improvementAllowance: {
            ...constraints.number,
        },
        askingRentMax: {
            ...constraints.number,
        },
        askingRentPerMonth: {
            ...constraints.number,
        },
        baseRentPerYear: {
            ...constraints.number,
        },
        nnnPerMonth: {
            ...constraints.number,
        },
        nnnPerYear: {
            ...constraints.number,
        },
        totalMonthlyRent: {
            ...constraints.number,
        },
        totalRentPerYear: {
            ...constraints.number,
        },
    },
};

const validateListingBroker = (listingBroker) => {
    return validate(listingBroker, outsideListingBroker, { fullMessages: false });
};

const validateMixedUse = (use) => {
    return validate(use, mixedUseConstraints, { fullMessages: false });
};

const validatePortfolio = (portfolio) => {
    return validate(portfolio, portfolioConstraints, { fullMessages: false });
};

const validateCondo = (condo) => {
    return validate(condo, condoConstraints, { fullMessages: false });
};

const validateSingleValue = (field, value, constraint) => {
    return validate({ [field]: value }, constraint, { fullMessages: false });
};

const validateListingBrokers = (listingBrokers) => {
    if (!listingBrokers) {
        return { "listingBrokers.eitherLogic": ["One of either km broker or outside broker must be present"] };
    } else {
        let listingBrokerErrors = {};
        let kmEmpty = true;
        let outsideEmpty = true;
        each(get(listingBrokers, "kmListingBrokers", []), (l, i) => {
            if (get(l, "brokerAgentKey.brokerAgentKey")) {
                kmEmpty = false;
            }
        });
        let counter = 0;
        each(get(listingBrokers, "outsideListingBrokers", []), (l, i) => {
            if (
                get(l, "brokerAgentKey.person.firstName") ||
                get(l, "brokerAgentKey.person.lastName") ||
                get(l, "company.displayName")
            ) {
                outsideEmpty = false;
                const validationResponse = validateListingBroker(l);
                if (isEmpty(validationResponse)) {
                    return;
                } else {
                    set(listingBrokerErrors, `outside.${counter}`, {
                        ...mapValues(validationResponse, (value, key) => {
                            return value[0];
                        }),
                    });
                }
            }
            counter += 1;
        });
        if (kmEmpty && outsideEmpty) {
            set(listingBrokerErrors, `eitherLogic`, "One of either km broker or outside broker must be present");
        }
        return isEmpty(listingBrokerErrors) ? {} : { listingBrokers: listingBrokerErrors };
    }
};

const validateListingBrokersWhenCombined = (listingBrokers) => {
    if (!listingBrokers) {
        return { "listingBrokers.eitherLogic": ["One of either km broker or outside broker must be present"] };
    } else {
        let listingBrokerErrors = {};
        let kmEmpty = true;
        let outsideEmpty = true;
        let counter = 0;
        each(listingBrokers, (l, i) => {
            if (get(l, "brokerAgentKey.kmBrokerFlag")) {
                if (get(l, "brokerAgentKey.brokerAgentKey")) {
                    kmEmpty = false;
                }
            } else {
                if (
                    get(l, "brokerAgentKey.person.firstName") ||
                    get(l, "brokerAgentKey.person.lastName") ||
                    get(l, "company.displayName")
                ) {
                    outsideEmpty = false;
                    const validationResponse = validateListingBroker(l);
                    if (isEmpty(validationResponse)) {
                        return;
                    } else {
                        set(listingBrokerErrors, `outside.${counter}`, {
                            ...mapValues(validationResponse, (value, key) => {
                                return value[0];
                            }),
                        });
                    }
                }
                counter += 1;
            }
        });
        if (kmEmpty && outsideEmpty) {
            set(listingBrokerErrors, `eitherLogic`, "One of either km broker or outside broker must be present");
        }
        return isEmpty(listingBrokerErrors) ? {} : { "listing.listingBrokers": listingBrokerErrors };
    }
};

const validateMixedUses = (mixedUses) => {
    let mixedUseErrors = {};
    let counter = 0;
    each(mixedUses, (m, i) => {
        const validationResponse = validateMixedUse(m);
        if (isEmpty(validationResponse)) {
            return;
        } else {
            set(mixedUseErrors, `${counter}`, {
                ...mapValues(validationResponse, (value, key) => {
                    return value[0];
                }),
            });
        }
        counter += 1;
    });
    return isEmpty(mixedUseErrors) ? {} : { "attributes.mixedUses": mixedUseErrors };
};

const validatePortfolioProperties = (properties) => {
    let errors = {};
    let counter = 0;
    each(properties, (p, i) => {
        const validationResponse = validatePortfolio(p);
        if (isEmpty(validationResponse)) {
            return;
        } else {
            set(errors, `${counter}`, {
                ...mapValues(validationResponse, (value, key) => {
                    return value[0];
                }),
            });
        }
        counter += 1;
    });
    return isEmpty(errors) ? {} : { "attributes.portfolioProperties": errors };
};

const validateCondos = (condos) => {
    let errors = {};
    let counter = 0;
    each(condos, (c, i) => {
        const validationResponse = validateCondo(c);
        if (isEmpty(validationResponse)) {
            return;
        } else {
            set(errors, `${counter}`, {
                ...mapValues(validationResponse, (value, key) => {
                    return value[0];
                }),
            });
        }
        counter += 1;
    });
    return isEmpty(errors) ? {} : { "attributes.condos": errors };
};

const validateAutoRenew = (listing) => {
    if (get(listing, "autoRenewFlg") !== true) {
        const errors = validateSingleValue(
            "expirationDate",
            get(listing, "expirationDate"),
            listingExpirationConstraint
        );
        return isEmpty(errors) ? {} : { expirationDate: true };
    }
    return {};
};

const validateListing = (listing, useType, transactionType) => {
    let whatToValidate = [];
    if (transactionType.toLowerCase() === "lease") {
        whatToValidate = [useType, transactionType, "listingInformation", "parkingInformation", "listingFinancial"];
    } else if (transactionType.toLowerCase() === "sale") {
        whatToValidate = [useType, transactionType, "listingInformation", "parkingInformation"];
    }

    let combinedConstraints = {};
    each(whatToValidate, (section) => {
        combinedConstraints = { ...combinedConstraints, ...listingConstraints[section] };
    });

    let setErrors = {};
    let errors = {
        ...mapValues(validate(listing, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...validateMixedUses(get(listing, "attributes.mixedUses")),
        ...validatePortfolioProperties(get(listing, "attributes.portfolioProperties")),
        ...validateCondos(get(listing, "attributes.condos")),
    };

    forIn(errors, (value, key) => {
        set(setErrors, key, value);
    });

    return isEmpty(setErrors) ? null : setErrors;
};

const validateListingByField = (section, id, value) => {
    switch (section) {
        case "mixedUse": {
            const { fullKey, result } = validateFieldWithKey(mixedUseConstraints, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
        case "condo": {
            const { fullKey, result } = validateFieldWithKey(condoConstraints, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
        case "portfolio": {
            const { fullKey, result } = validateFieldWithKey(portfolioConstraints, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
        default: {
            const { fullKey, result } = validateFieldWithKey(listingConstraints, id, value, section);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
    }
};

export default {
    validateListingBrokers,
    validateListingBrokersWhenCombined,
    validateAutoRenew,
    validateMixedUses,
    validatePortfolioProperties,
    validateCondos,
    validateListing,
    validateListingByField,
};
